import { Stats } from "fs";
import * as path from "path";

import { createFsFromVolume, IFs, Volume } from "memfs";
import { TEncodingExtended } from "memfs/lib/encoding";

import { IFileWriter } from "../../../../../BuildTasks/Form/GenerateForm";

import { IInputFileSystem } from "./IInputFileSystem";
import {
    FileSystemTimeInfoEntry,
    IWatchFileSystem,
    IWatchFileSystemWatcher,
    WatchFileSystemCallback,
    WatchFileSystemCallbackUndelayed,
    WatchFileSystemWatchOptions,
} from "./IWatchFileSystem";

export class WebpackHybridFileSystem implements IFileWriter, IInputFileSystem, IWatchFileSystem {
    private readonly pathToVirtualDirectory: string;
    private innerInputFileSystem!: IInputFileSystem;
    private readonly virtualFs: IFs;
    private readonly watchers: WebpackVirtualWatchFileSystemWatcher[] = [];

    public constructor(pathToVirtualDirectory: string) {
        this.pathToVirtualDirectory = pathToVirtualDirectory;
        const virtualVolume = Volume.fromJSON({});
        this.virtualFs = createFsFromVolume(virtualVolume);
    }

    public setInnerInputFileSystem(innerInputFileSystem: IInputFileSystem): void {
        if (this.innerInputFileSystem != undefined) {
            throw new Error("Cannot set inner file system twice");
        }
        this.innerInputFileSystem = innerInputFileSystem;
    }

    public readFile(
        filename: string,
        options: string | { encoding: string; flag?: string },
        callback: (err: NodeJS.ErrnoException, data: string) => void
    ): void;
    public readFile(
        filename: string,
        options: { flag?: string },
        callback: (err: NodeJS.ErrnoException, data: Buffer) => void
    ): void;
    public readFile(filename: string, callback: (err: NodeJS.ErrnoException, data: Buffer) => void): void;
    public readFile(
        filename: string,
        options:
            | string
            | { encoding: TEncodingExtended; flag?: string }
            | { flag?: string }
            | ((err: NodeJS.ErrnoException, data: Buffer) => void),
        callback?:
            | ((err: NodeJS.ErrnoException, data: string) => void)
            | ((err: NodeJS.ErrnoException, data: Buffer) => void)
    ): void {
        if (filename.startsWith(this.pathToVirtualDirectory)) {
            // @ts-ignore
            this.virtualFs.readFile(filename, options, callback);
        } else {
            // @ts-ignore
            this.innerInputFileSystem.readFile(filename, options, callback);
        }
    }

    public readFileSync(filename: string, options: string | { encoding: string; flag?: string }): string;
    public readFileSync(filename: string, options?: { flag?: string }): Buffer;
    public readFileSync(
        filename: string,
        options?: string | { encoding: string; flag?: string } | { flag?: string }
    ): string | Buffer {
        if (filename.startsWith(this.pathToVirtualDirectory)) {
            // @ts-ignore
            return this.virtualFs.readFileSync(filename, options);
        } else {
            // @ts-ignore
            return this.innerInputFileSystem.readFileSync(filename, options);
        }
    }

    public readdir(dirPath: string, callback: (err: Error, files: string[]) => void): void {
        if (dirPath.startsWith(this.pathToVirtualDirectory)) {
            // @ts-ignore
            this.virtualFs.readdir(dirPath, callback);
        } else {
            // @ts-ignore
            this.innerInputFileSystem.readdir(dirPath, callback);
        }
    }

    public readdirSync(dirPath: string): string[] {
        if (dirPath.startsWith(this.pathToVirtualDirectory)) {
            // @ts-ignore
            return this.virtualFs.readdirSync(dirPath);
        } else {
            // @ts-ignore
            return this.innerInputFileSystem.readdirSync(dirPath);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public readlink(dirPath: string, callback?: (err: NodeJS.ErrnoException, linkString: string) => any): void {
        if (dirPath.startsWith(this.pathToVirtualDirectory)) {
            // @ts-ignore
            this.virtualFs.readlink(dirPath, callback);
        } else {
            // @ts-ignore
            this.innerInputFileSystem.readlink(dirPath, callback);
        }
    }

    public readlinkSync(linkPath: string): string {
        if (linkPath.startsWith(this.pathToVirtualDirectory)) {
            // @ts-ignore
            return this.virtualFs.readlinkSync(linkPath);
        } else {
            // @ts-ignore
            return this.innerInputFileSystem.readlinkSync(linkPath);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public stat(fsPath: string, callback?: (err: NodeJS.ErrnoException, stats: Stats) => any): void {
        if (fsPath.startsWith(this.pathToVirtualDirectory)) {
            // @ts-ignore
            this.virtualFs.stat(fsPath, callback);
        } else {
            // @ts-ignore
            this.innerInputFileSystem.stat(fsPath, callback);
        }
    }

    public statSync(fsPath: string): Stats {
        if (fsPath.startsWith(this.pathToVirtualDirectory)) {
            // @ts-ignore
            return this.virtualFs.statSync(fsPath);
        } else {
            // @ts-ignore
            return this.innerInputFileSystem.statSync(fsPath);
        }
    }

    public async writeBatch(fileBatch: { [p: string]: string }): Promise<void> {
        for (const filePath of Object.keys(fileBatch)) {
            this.virtualFs.mkdirpSync(path.parse(filePath).dir);
            // @ts-ignore Там баг в тайпингах
            this.virtualFs.writeFileSync(filePath, fileBatch[filePath], "utf8");
        }
        this.emitChanges(fileBatch);
    }

    public watch(
        files: ILazySet<string>,
        dirs: ILazySet<string>,
        missing: ILazySet<string>,
        _startTime: number,
        _options: WatchFileSystemWatchOptions,
        callback: WatchFileSystemCallback,
        callbackUndelayed: WatchFileSystemCallbackUndelayed
    ): IWatchFileSystemWatcher {
        const result = new WebpackVirtualWatchFileSystemWatcher(
            this.virtualFs,
            [...files.values()].filter(x => x.startsWith(this.pathToVirtualDirectory)),
            [...dirs.values()].filter(x => x.startsWith(this.pathToVirtualDirectory)),
            [...missing.values()].filter(x => x.startsWith(this.pathToVirtualDirectory)),
            callback,
            callbackUndelayed
        );
        this.watchers.push(result);
        return result;
    }

    private emitChanges(fileBatch: { [p: string]: string }): void {
        for (const watcher of this.watchers) {
            watcher.emitChanges(Object.keys(fileBatch));
        }
    }
}

class WebpackVirtualWatchFileSystemWatcher implements IWatchFileSystemWatcher {
    private readonly virtualFs: IFs;
    private readonly files: string[];
    private readonly dirs: string[];
    private readonly missing: string[];
    private readonly callback: WatchFileSystemCallback;
    private readonly callbackUndelayed: WatchFileSystemCallbackUndelayed;

    private closed = false;
    private paused = false;

    public constructor(
        virtualFs: IFs,
        files: string[],
        dirs: string[],
        missing: string[],
        callback: WatchFileSystemCallback,
        callbackUndelayed: WatchFileSystemCallbackUndelayed
    ) {
        this.virtualFs = virtualFs;
        this.files = files;
        this.dirs = dirs;
        this.missing = missing;
        this.callback = callback;
        this.callbackUndelayed = callbackUndelayed;
    }

    public emitChanges(filePaths: string[]): void {
        if (this.paused || this.closed) {
            return;
        }
        const changes: Set<string> = new Set();
        if (filePaths[0] != undefined) {
            this.callbackUndelayed(filePaths[0], Date.now());
        }
        for (const filePath of filePaths) {
            changes.add(filePath);
        }

        this.emitAggregatedChanges(changes);
    }

    public close(): void {
        this.closed = true;
    }

    public pause(): void {
        this.paused = true;
    }

    public getContextTimeInfoEntries(): Map<string, FileSystemTimeInfoEntry> {
        return this.getFileTimeInfoEntries();
    }

    public getFileTimeInfoEntries(): Map<string, FileSystemTimeInfoEntry> {
        const times: Map<string, FileSystemTimeInfoEntry> = new Map();
        for (const file of this.files) {
            times.set(file, {
                timestamp: this.virtualFs.statSync(file).mtimeMs,
                safeTime: this.virtualFs.statSync(file).mtimeMs,
            });
        }
        return times;
    }

    private emitAggregatedChanges(changes: Set<string>): void {
        const filePaths = Array.from(changes);
        const files = filePaths.filter(filePath => this.files.includes(filePath)).sort();
        const dirs = filePaths.filter(filePath => this.dirs.includes(filePath)).sort();
        const missing = filePaths.filter(filePath => this.missing.includes(filePath)).sort();
        const times = this.getFileTimeInfoEntries();
        // @ts-ignore
        this.callback(undefined, files, dirs, missing, times, times, new Set());
    }
}

interface ILazySet<T> {
    values(): Iterable<T>;
}
